import React from "react";

import { useDataset } from "../terrain3d/datapack";
import { CHART_SPECS, getChartSpec, paramOptions, resolveParams } from "../terrain3d/chartSpecs";

/**
 * INSPECTEUR DES GRAPHIQUES D'UNE ETAPE.
 *
 * IL NE CONNAIT AUCUN GRAPHIQUE EN PARTICULIER. Les reglages qu'il affiche
 * sont lus dans la specification choisie (chartSpecs.js : `params`), et leurs
 * options sont calculees sur le VRAI jeu de donnees du territoire — les annees
 * reellement renseignees dans la BNPE, les stations reellement instrumentees,
 * les variables reellement presentes dans le datapack. C'est ce qui rend un
 * graphique editable au sens ou l'entend l'auteur : remplacer les
 * precipitations par l'humidite des sols est un choix dans une liste, pas une
 * demande de developpement.
 *
 * Ajouter un graphique au catalogue n'ajoute donc pas une ligne ici.
 *
 * PAS D'APERCU DANS CE PANNEAU : le canevas, a gauche, monte le vrai panneau
 * d'etape avec les vrais graphiques. Un second aperçu, plus petit, dirait la
 * meme chose en moins bien.
 */

function newChartUid() {
  return `c_${Math.random().toString(36).slice(2, 10)}`;
}

export function defaultChart() {
  // Aucun reglage pose : la specification fournira ses defauts une fois la
  // donnee chargee (cf. resolveParams). Un graphique tout juste ajoute affiche
  // donc immediatement quelque chose de sense.
  return { chart_uid: newChartUid(), chart_id: CHART_SPECS[0].id, params: {} };
}

/** Un reglage, rendu d'apres son type. */
function ParamField({ param, value, options, onChange }) {
  if (param.type === "toggle") {
    return (
      <label className="builder-label builder-label-inline">
        <input
          type="checkbox"
          checked={value !== false}
          onChange={(event) => onChange(event.target.checked)}
        />
        {" "}{param.label}
      </label>
    );
  }

  if (param.type === "multi") {
    const selected = Array.isArray(value) ? value : [];
    return (
      <div className="builder-label">
        <span className="builder-label-text">{param.label}</span>
        <div className="step-editor-layer-checks">
          {options.map((option) => (
            <label key={option.value} className="builder-label builder-label-inline">
              <input
                type="checkbox"
                checked={selected.includes(option.value)}
                onChange={() => onChange(
                  selected.includes(option.value)
                    ? selected.filter((item) => item !== option.value)
                    : [...selected, option.value]
                )}
              />
              {" "}{option.label}
            </label>
          ))}
          {options.length === 0 && <span className="builder-hint">Aucune valeur dans ce jeu de données.</span>}
        </div>
      </div>
    );
  }

  return (
    <label className="builder-label">
      <span className="builder-label-text">{param.label}</span>
      <select
        className="builder-input"
        value={value === undefined || value === null ? "" : String(value)}
        onChange={(event) => {
          // Les valeurs remontent en chaine depuis un <select> ; une annee doit
          // redevenir un nombre, sans quoi la comparaison au millesime des
          // series echouerait silencieusement.
          const raw = event.target.value;
          const match = options.find((option) => String(option.value) === raw);
          onChange(match ? match.value : raw);
        }}
      >
        {options.map((option) => (
          <option key={String(option.value)} value={String(option.value)}>{option.label}</option>
        ))}
      </select>
    </label>
  );
}

/** Un graphique de la liste : son type, son titre, ses reglages. */
function ChartRow({ territoryId, chart, index, count, onChange, onRemove, onMove }) {
  const spec = getChartSpec(chart.chart_id);
  const { data, loading, error } = useDataset(territoryId, spec?.dataset || null);

  // Reglages EFFECTIFS : ce que l'auteur a pose, complete par les defauts de la
  // specification. L'inspecteur montre ainsi ce que le graphique fait
  // reellement, et non des champs vides devant une courbe deja tracee.
  const effective = resolveParams(spec, data, chart.params);

  const setParam = (paramId, value) => {
    onChange(index, { ...chart, params: { ...(chart.params || {}), [paramId]: value } });
  };

  return (
    <div className="step-editor-card-row">
      <div className="builder-section-header">
        <select
          className="builder-input"
          value={chart.chart_id}
          onChange={(event) => onChange(index, {
            // Changer de graphique remet les reglages a plat : ceux d'un
            // camembert de prelevements n'ont aucun sens sur une courbe de
            // debit, les garder ne ferait que trainer des cles mortes en base.
            ...chart, chart_id: event.target.value, params: {},
          })}
        >
          {CHART_SPECS.map((option) => (
            <option key={option.id} value={option.id}>{option.label}</option>
          ))}
        </select>
        <div className="step-editor-card-tools">
          <button
            className="builder-icon-btn"
            disabled={index === 0}
            onClick={() => onMove(index, -1)}
            title="Monter"
          >↑</button>
          <button
            className="builder-icon-btn"
            disabled={index === count - 1}
            onClick={() => onMove(index, 1)}
            title="Descendre"
          >↓</button>
          <button
            className="builder-icon-btn danger"
            onClick={() => onRemove(index)}
            title="Retirer ce graphique"
          >✕</button>
        </div>
      </div>

      {spec?.hint && <p className="step-editor-section-hint">{spec.hint}</p>}

      <label className="builder-label">
        <span className="builder-label-text">Titre affiché (optionnel)</span>
        <input
          className="builder-input"
          value={chart.title || ""}
          onChange={(event) => onChange(index, { ...chart, title: event.target.value })}
          placeholder="Laisser vide : le graphique se nomme lui-même"
        />
      </label>

      <label className="builder-label">
        <span className="builder-label-text">Texte d'accompagnement (optionnel)</span>
        <textarea
          className="builder-textarea"
          rows={3}
          value={chart.text || ""}
          onChange={(event) => onChange(index, { ...chart, text: event.target.value })}
          placeholder="Ce qu'il faut retenir du graphique — affiché sous le tracé, avant la source."
        />
      </label>

      {loading && <p className="builder-hint">Lecture du jeu de données…</p>}
      {error && (
        <p className="step-editor-status is-warn">
          Le fichier <code>{spec.dataset}</code> est absent du datapack de ce territoire :
          ce graphique restera vide en séance.
        </p>
      )}

      {!loading && !error && (spec?.params || []).map((param) => (
        <div key={param.id}>
          <ParamField
            param={param}
            value={effective[param.id]}
            options={paramOptions(param, data)}
            onChange={(value) => setParam(param.id, value)}
          />
          {param.hint && <p className="builder-hint">{param.hint}</p>}
        </div>
      ))}
    </div>
  );
}

export default function ChartInspector({ territoryId, charts, onChange }) {
  const list = charts || [];

  const update = (index, next) => {
    const copy = [...list];
    copy[index] = next;
    onChange(copy);
  };

  const remove = (index) => {
    const copy = [...list];
    copy.splice(index, 1);
    onChange(copy);
  };

  const move = (index, delta) => {
    const target = index + delta;
    if (target < 0 || target >= list.length) return;
    const copy = [...list];
    [copy[index], copy[target]] = [copy[target], copy[index]];
    onChange(copy);
  };

  if (!territoryId) {
    return <div className="builder-empty">Choisissez d'abord un territoire pour cette page.</div>;
  }

  return (
    <>
      <div className="builder-section-header">
        <h3 className="builder-subsection-title">Graphiques ({list.length})</h3>
        <button className="builder-btn-secondary" onClick={() => onChange([...list, defaultChart()])}>
          + Ajouter
        </button>
      </div>

      <p className="step-editor-section-hint">
        Ces graphiques lisent les données du territoire — les mêmes fichiers que la carte.
        Deux au maximum côte à côte restent lisibles au fond d'une salle.
      </p>

      {list.length === 0 && (
        <div className="builder-empty">Aucun graphique. Cliquez sur « + Ajouter ».</div>
      )}

      {list.map((chart, index) => (
        <ChartRow
          key={chart.chart_uid || index}
          territoryId={territoryId}
          chart={chart}
          index={index}
          count={list.length}
          onChange={update}
          onRemove={remove}
          onMove={move}
        />
      ))}
    </>
  );
}
